import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Selector } from 'antd-mobile';
import { useLocation } from 'react-router';
import { Icon } from '@fuxi/eevee-icon';
import { Skeleton, Tooltip } from 'antd';
import { useUpdateEffect } from 'ahooks';
import { Toast } from '@/utils';
import { OneMoreOrderParams } from '@/pages/user/service-detail';
import { PartLists } from '@/constant';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { CommonSelectorStyle, TherapyPlanList } from '../../constant';
import { ClassifyPlanListType } from '../../index';
import cx from './index.module.scss';

export interface FilterDimension {
  bodyPart: Service.BodyPart;
  planType: Massage.MassageItem;
}

interface FilterHeaderProps {
  classifyPlanList: ClassifyPlanListType;
  onFilter: React.Dispatch<React.SetStateAction<FilterDimension>>;
  isGettingProducts: boolean;
  scrollViewRef: React.MutableRefObject<ScrollViewRefAttrs>;
  tooltipPartOpen: Record<number, boolean>;
  setTooltipPartOpen: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  therapyPlanOpen: Record<number, boolean>;
  setTooltipTherapyPlanOpen: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
}

const FilterHeader = ({
  classifyPlanList,
  onFilter,
  isGettingProducts,
  tooltipPartOpen,
  setTooltipPartOpen,
  therapyPlanOpen,
  setTooltipTherapyPlanOpen,
  scrollViewRef,
}: FilterHeaderProps) => {
  const [isExpand, setIsExpand] = useState<boolean>(true);
  const [currentSelectBodyPart, setCurrentSelectBodyPart] = useState<Service.BodyPart>(null);
  const [currentSelectPlanType, setCurrentSelectPlanType] = useState<Massage.MassageItem>(null);
  const { state } = useLocation() as {
    state: OneMoreOrderParams & {
      selectedInfoByUser: FilterDimension;
    };
  };

  const handleExpandFilterInfo = useCallback(() => {
    setIsExpand(!isExpand);
    scrollViewRef.current?.handleRefresh();
  }, [isExpand, scrollViewRef]);

  const bodyPartsOptions = useMemo<
    {
      index: number;
      value: Service.BodyPart;
      disabled: boolean;
      label: JSX.Element;
    }[]
  >(() => {
    let NotAllowedPart: Service.BodyPart[] = null;
    if (currentSelectPlanType) {
      const allSelectedPlanLists = classifyPlanList?.byPlanType.get(currentSelectPlanType);
      /** 筛选出当前所选的理疗类型不包含的理疗部位 */
      NotAllowedPart = PartLists.map(item =>
        allSelectedPlanLists?.some(item2 => item2.bodyPart === item.partName || item2.bodyPart === 'All')
          ? null
          : (item.partName as Service.BodyPart)
      )?.filter(Boolean);
      // 磁震波排除腹部理疗
      if (currentSelectPlanType === 'Shockwave') {
        NotAllowedPart.push('Abdomen');
      }
    }
    return PartLists.filter(item => classifyPlanList?.byBodyPart.get(item.partName as Service.BodyPart)?.length).map(
      (item, index) => {
        // 再来一单仅可选择订单信息中的部位，禁用其它部位
        const currentPartIsDisabled = state?.storeGoodsId
          ? item.partName !== state?.bodyPart
          : NotAllowedPart?.includes(item.partName as Service.BodyPart);

        return {
          index: index,
          value: item.partName as Service.BodyPart,
          disabled: currentPartIsDisabled,
          label: currentPartIsDisabled ? (
            <Tooltip placement="top" title={'当前' + item.name + '不可选'} open={tooltipPartOpen[index] === true}>
              <div
                onClick={() => {
                  if (state?.storeGoodsId) {
                    Toast.show('不支持切换其它方案');
                    return;
                  }
                  setTooltipPartOpen({ ...tooltipPartOpen, [index]: true });
                }}
              >
                {item.name}
              </div>
            </Tooltip>
          ) : (
            <span>{item.name}</span>
          ),
        };
      }
    );
  }, [classifyPlanList, currentSelectPlanType, setTooltipPartOpen, state, tooltipPartOpen]);

  const therapyPlanListOption = useMemo(() => {
    let NotAllowedPlanType: Massage.MassageItem[] = null;

    if (currentSelectBodyPart) {
      const allSelectedBodyLists = [
        ...(classifyPlanList?.byBodyPart.get(currentSelectBodyPart) || []),
        ...(classifyPlanList?.byBodyPart.get('All') || []),
      ];
      /** 筛选出当前所选的理疗部位不包含的理疗类型 */
      NotAllowedPlanType = TherapyPlanList.map(item =>
        allSelectedBodyLists?.some(item2 => item2?.items?.[0] === item.key) ? null : (item.key as Massage.MassageItem)
      )?.filter(Boolean);

      // 磁震波排除腹部理疗
      if (currentSelectBodyPart === 'Abdomen') {
        NotAllowedPlanType.push('Shockwave');
      }
    }

    return TherapyPlanList.filter(
      item => classifyPlanList?.byPlanType.get(item.key as Massage.MassageItem)?.length
    ).map((item, index) => {
      // 再来一单仅可选择订单信息中的理疗类型，禁用其它理疗类型
      const currentPlanTypeIsDisabled = state?.storeGoodsId
        ? item.key !== state?.massageItem
        : NotAllowedPlanType?.includes(item.key as Massage.MassageItem);

      return {
        index: index,
        value: item.key as Massage.MassageItem,
        disabled: currentPlanTypeIsDisabled,
        label: currentPlanTypeIsDisabled ? (
          <Tooltip placement="top" title={'当前' + item.name + '理疗不可选'} open={therapyPlanOpen[index] === true}>
            <div
              onClick={() => {
                if (state?.storeGoodsId) {
                  Toast.show('不支持切换其它方案');
                  return;
                }
                setTooltipTherapyPlanOpen({ ...therapyPlanOpen, [index]: true });
              }}
            >
              {item.name + '理疗'}
            </div>
          </Tooltip>
        ) : (
          <span>{item.name + '理疗'}</span>
        ),
      };
    });
  }, [classifyPlanList, currentSelectBodyPart, setTooltipTherapyPlanOpen, state, therapyPlanOpen]);

  useUpdateEffect(() => {
    onFilter?.({
      bodyPart: currentSelectBodyPart,
      planType: currentSelectPlanType,
    });
  }, [currentSelectBodyPart, currentSelectPlanType]);

  useEffect(() => {
    if (state?.selectedInfoByUser) {
      setCurrentSelectBodyPart(state?.selectedInfoByUser?.bodyPart);
      setCurrentSelectPlanType(state?.selectedInfoByUser?.planType);
    }
  }, [state?.selectedInfoByUser]);

  return (
    <div className={cx('title')}>
      <div className={cx('therapy-title')}>
        <div>选择理疗方案</div>
        <div onClick={handleExpandFilterInfo} className={cx('filter-box')}>
          <span className={cx('filter-title')}>筛选</span>
          <Icon className={cx('icon')} name={isExpand ? 'arrow-up' : 'arrow-down'} />
        </div>
      </div>
      {isExpand && (
        <div className={cx('filter-info')}>
          <Skeleton active paragraph={{ rows: 1 }} loading={isGettingProducts}>
            <div className={cx('filter-info-box')}>
              <div className={cx('filter-info-title')}>理疗部位：</div>
              <Selector
                options={bodyPartsOptions}
                className={cx('filter-info-selector')}
                style={CommonSelectorStyle}
                defaultValue={[currentSelectBodyPart]}
                showCheckMark={false}
                columns={bodyPartsOptions?.length}
                {...(state?.bodyPart ? { value: [state?.bodyPart] } : {})}
                onChange={parts => {
                  if (state?.bodyPart) return;
                  setCurrentSelectBodyPart(parts?.[0] || null);
                }}
              />
            </div>
            <div className={cx('filter-info-box', 'extra-margin-top')}>
              <div className={cx('filter-info-title')}>理疗类型：</div>
              <Selector
                options={therapyPlanListOption}
                className={cx('filter-info-selector')}
                style={CommonSelectorStyle}
                defaultValue={[currentSelectPlanType]}
                showCheckMark={false}
                columns={therapyPlanListOption?.length}
                {...(state?.massageItem ? { value: [state?.massageItem] } : {})}
                onChange={types => {
                  if (state?.massageItem) return;
                  setCurrentSelectPlanType(types?.[0] || null);
                }}
              />
            </div>
          </Skeleton>
        </div>
      )}
    </div>
  );
};

export default FilterHeader;
